import { ImageOff } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { parseFileRecordContent } from '../../../lib/file-record';
import { wixelEditUrl } from '../../../lib/wixel-link';
import { Button, ScrollArea, Typography } from '../ui';
import styles from './DocViewerModal.module.css';

// A "wixel asset" — a Wix design-tool asset carrying a preview image + viewer link + editor link. Each of
// the 3 header URLs is handled INDEPENDENTLY absent (per the classifier's own robustness note in
// `lib/doc-viewer.ts`): a missing preview gets a placeholder graphic instead of a blank image; a missing
// viewer or editor link hides that specific button rather than rendering a dead one.
export function WixelDocView({ content }: { content?: string | null }) {
  const { t } = useTranslation();
  const { header } = parseFileRecordContent(content);
  const previewUrl = header?.wixelPreviewUrl?.trim() || null;
  const viewerUrl = header?.wixelViewerUrl?.trim() || null;
  // Falls back to the asset's own short link + `/edit` when nothing stamped the header (see `lib/wixel-link.ts`).
  const editorUrl = wixelEditUrl(content);

  if (!previewUrl && !viewerUrl && !editorUrl) {
    return (
      <div className={styles.state}>
        <Typography variant="p2" color="secondary" component="p">
          {t('docViewer.wixelUnavailable')}
        </Typography>
      </div>
    );
  }

  return (
    <ScrollArea className={styles.mediaBody}>
      {previewUrl ? (
        <img src={previewUrl} alt="" className={styles.mediaImage} />
      ) : (
        <div className={styles.wixelPlaceholder}>
          <ImageOff size={32} aria-hidden />
        </div>
      )}
      {/* Links are useless on paper, and `@media print` cannot name a CSS-module class — hence the attribute. */}
      <div className={styles.wixelActions} data-print-hide>
        {viewerUrl && (
          <Button variant="outlined" href={viewerUrl} external target="_blank" rel="noopener noreferrer">
            {t('fileActions.openInWixel')}
          </Button>
        )}
        {editorUrl && (
          <Button variant="outlined" href={editorUrl} external target="_blank" rel="noopener noreferrer">
            {t('fileActions.editInWixel')}
          </Button>
        )}
      </div>
    </ScrollArea>
  );
}
